import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, Context, WorkflowStepInterface } from '@vendasta/automata';
import { Statuses } from '@vendasta/campaigns';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CampaignSelectorService } from '../../../../data-services/campaign-selector.service';
import {
  pauseCampaignForCompanyTaskDefinitionId,
  pauseCampaignForContactTaskDefinitionId,
  StartCampaignAction,
} from '../../../component-loader/constants';
import { AutomationErrorHandlerService } from '../../../services';
import { CommonDisplayActionService } from '../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../common/common-display-activity.service';
import { AutomationsRouterService } from '../../../../shared/automations-router.service';
import { AutomataContextService } from '@galaxy/automata/shared';

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example AddTagService
 */

interface translationStrings {
  actionLinked: string;
  actionDefault: string;
  activityLinked: string;
  activityDefault: string;
  activityFailed: string;
  selectorHint?: string;
}

export const stepTranslations: { [key: string]: translationStrings } = {
  'TaskDefinition-add-contact-campaign': {
    actionLinked: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_GENERAL.CONTACT.ACTION_LINKED',
    actionDefault: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_GENERAL.CONTACT.ACTION_DEFAULT',
    activityLinked: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_GENERAL.CONTACT.ACTIVITY_LINKED',
    activityDefault: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_GENERAL.CONTACT.ACTIVITY_DEFAULT',
    activityFailed: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_GENERAL.CONTACT.ACTIVITY_FAILED',
  },
  [pauseCampaignForContactTaskDefinitionId]: {
    actionLinked: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_CONTACT.ACTION_LINKED',
    actionDefault: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_CONTACT.ACTION_DEFAULT',
    activityLinked: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_CONTACT.ACTIVITY_LINKED',
    activityDefault: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_CONTACT.ACTIVITY_DEFAULT',
    activityFailed: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_CONTACT.ACTIVITY_FAILED',
  },
  [pauseCampaignForCompanyTaskDefinitionId]: {
    actionLinked: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_COMPANY.ACTION_LINKED',
    actionDefault: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_COMPANY.ACTION_DEFAULT',
    activityLinked: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_COMPANY.ACTIVITY_LINKED',
    activityDefault: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_COMPANY.ACTIVITY_DEFAULT',
    activityFailed: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.PAUSE_CAMPAIGN_FOR_COMPANY.ACTIVITY_FAILED',
  },
  [StartCampaignAction]: {
    actionLinked: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_GENERAL.ACTION_LINKED',
    actionDefault: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_GENERAL.ACTION_DEFAULT',
    activityLinked: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_GENERAL.ACTIVITY_LINKED',
    activityDefault: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_GENERAL.ACTIVITY_DEFAULT',
    activityFailed: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_GENERAL.ACTIVITY_FAILED',
    selectorHint: 'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_GENERAL.HINT',
  },
};

@Injectable({ providedIn: 'root' })
export class StartCampaignService implements CommonDisplayActivityService, CommonDisplayActionService {
  constructor(
    private readonly translateService: TranslateService,
    public sanitizer: DomSanitizer,
    private readonly campaignsService: CampaignSelectorService,
    protected readonly errorHandler: AutomationErrorHandlerService,
    protected readonly routerService: AutomationsRouterService,
    private readonly automationContextService: AutomataContextService,
  ) {}

  getActivitySubtitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return of('');
  }

  getActivityTitle(_namespace: string, activity: ActivityInterface): Observable<string> {
    const translations = stepTranslations?.[activity.workflowStep.taskDefinitionId];
    const activityData = activity?.activityData ? JSON.parse(activity.activityData) : {};

    const campaignUrl = this.createCampaignPath(activityData?.campaign_id);

    return combineLatest([
      this.campaignsService.get(activityData?.campaign_id),
      this.routerService.applyRoutePrefix(campaignUrl),
    ]).pipe(
      map(([campaign, campaignUrl]) => {
        if (campaign) {
          return this.translateService.instant(translations.activityLinked, {
            campaignId: campaign.id,
            campaignName: campaign.name,
            campaignUrl: campaignUrl,
          });
        }
        return this.translateService.instant(translations.activityDefault);
      }),
    );
  }

  getTemplateActionSubtitle(_namespace: string, step: WorkflowStepInterface): Observable<string> {
    return this.getActionSubtitleEmptyState(step);
  }

  getActionSubtitle(_namespace: string, step: WorkflowStepInterface): Observable<string> {
    const data = step ? JSON.parse(step?.data || '') : {};
    if (!data.campaign_id) {
      this.errorHandler.setValidationError({
        stepId: step?.id || '',
        taskDefinitionId: step?.taskDefinitionId || '',
        errorMessage: this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_GENERAL.CONFIG_ERROR'),
        isNonDismissable: true,
      });
      return this.getActionSubtitleEmptyState(step);
    }

    const campaignUrl = this.createCampaignPath(data.campaign_id);

    return combineLatest([
      this.campaignsService.get(data?.campaign_id),
      this.routerService.applyRoutePrefix(campaignUrl),
    ]).pipe(
      switchMap(([campaign, url]) => {
        if (campaign?.status === Statuses.STATUSES_PUBLISHED || campaign?.status === Statuses.STATUSES_ACTIVE) {
          return this.getActionSubtitleSuccessState(step.taskDefinitionId, {
            campaignId: campaign.id,
            campaignName: campaign.name,
            campaignUrl: url,
          });
        }
        return this.getActionSubtitleErrorState(campaign, step);
      }),
    );
  }

  getActionSubtitleSuccessState(
    taskDefinitionId: string,
    { campaignId, campaignName, campaignUrl }: any,
  ): Observable<string> {
    const translation =
      stepTranslations?.[taskDefinitionId]?.actionLinked ||
      'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_GENERAL.ACTION_NO_ENTITY_LINKED';
    return this.translateService.stream(translation, {
      campaignId: campaignId,
      campaignUrl: campaignUrl,
      campaignName: campaignName,
    });
  }

  getActionSubtitleErrorState(campaign: any, step: WorkflowStepInterface): Observable<string> {
    const translations = stepTranslations?.[step.taskDefinitionId];
    let errorMessage = this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.START_GENERAL.CONFIG_ERROR');
    if (campaign?.status === 'archived') {
      errorMessage = this.translateService.instant(
        'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.COMMON.CONFIG_ERROR.ARCHIVED_ERROR',
      );
    }
    if (campaign?.status === 'draft') {
      errorMessage = this.translateService.instant(
        'AUTOMATIONS.EDITOR.TASKS.CAMPAIGNS.COMMON.CONFIG_ERROR.DRAFT_ERROR',
      );
    }
    this.errorHandler.setValidationError({
      stepId: step?.id || '',
      taskDefinitionId: step?.taskDefinitionId || '',
      errorMessage: errorMessage,
      isNonDismissable: true,
    });

    if (campaign) {
      const campaignUrl = this.createCampaignPath(campaign.id);
      return this.routerService.applyRoutePrefix(campaignUrl).pipe(
        switchMap((url) => {
          return this.translateService.stream(translations.actionLinked, {
            campaignId: campaign.id,
            campaignName: campaign.name,
            campaignUrl: url,
          });
        }),
      );
    }

    return this.translateService.stream(translations.actionDefault);
  }

  getActionSubtitleEmptyState(step: WorkflowStepInterface): Observable<string> {
    const translations = stepTranslations?.[step.taskDefinitionId];
    return this.translateService.stream(translations.actionDefault);
  }

  private createCampaignPath(campaignId: string): string {
    const currentContext = this.automationContextService.currentContext;
    return currentContext === Context.AUTOMATION_CONTEXT_SMB
      ? `/campaigns/${campaignId}/details`
      : `marketing/campaign/details/${campaignId}`;
  }
}
